// store/progressStore.ts
import { create } from 'zustand';
import { progressService } from '../services/progressService';
import type { VideoProgress, VideoStatus } from '../types/progress';

interface LastWatched {
  videoId: string;
  subjectId: string;
  chapterName: string;
  lessonTitle: string;
}

interface ProgressState {
  videoProgress: Record<string, VideoProgress>;
  overallProgress: number;
  lastWatched: LastWatched | null;
  isLoading: boolean;
  fetchProgress: (userId: string) => Promise<void>;
  updateVideoStatus: (userId: string, videoId: string, status: VideoStatus) => Promise<void>;
  getVideoStatus: (videoId: string) => VideoStatus;
  calculateProgress: (userId: string, totalVideos: number) => Promise<void>;
  setLastWatched: (info: LastWatched) => void;
  getRecommendedLessonIndex: (videoIds: string[]) => number;
}

const LAST_WATCHED_KEY = 'cn_last_watched';
const loadLastWatched = (): LastWatched | null => {
  try {
    const raw = localStorage.getItem(LAST_WATCHED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const useProgressStore = create<ProgressState>((set, get) => ({
  videoProgress: {},
  overallProgress: 0,
  lastWatched: loadLastWatched(),
  isLoading: false,

  fetchProgress: async (userId: string) => {
    set({ isLoading: true });
    try {
      const progress = await progressService.getUserProgress(userId);
      const progressMap: Record<string, VideoProgress> = {};
      progress.forEach(p => {
        progressMap[p.video_id] = p;
      });
      set({ videoProgress: progressMap, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  updateVideoStatus: async (userId: string, videoId: string, status: VideoStatus) => {
    try {
      await progressService.updateVideoProgress(userId, videoId, status);
      const currentProgress = get().videoProgress;
      set({
        videoProgress: {
          ...currentProgress,
          [videoId]: {
            ...currentProgress[videoId],
            video_id: videoId,
            user_id: userId,
            status,
            updated_at: new Date().toISOString(),
            last_position: 0,
            id: currentProgress[videoId]?.id || '',
          },
        },
      });
    } catch {
      // Silently fail
    }
  },

  getVideoStatus: (videoId: string): VideoStatus => {
    return get().videoProgress[videoId]?.status || 'not_started';
  },

  calculateProgress: async (userId: string, totalVideos: number) => {
    const progress = await progressService.calculateProgress(userId, totalVideos);
    set({ overallProgress: progress });
  },

  setLastWatched: (info: LastWatched) => {
    localStorage.setItem(LAST_WATCHED_KEY, JSON.stringify(info));
    set({ lastWatched: info });
  },

  // Find the index of the first non-completed video in an ordered list
  getRecommendedLessonIndex: (videoIds: string[]): number => {
    const { videoProgress } = get();
    for (let i = 0; i < videoIds.length; i++) {
      const status = videoProgress[videoIds[i]]?.status;
      if (status !== 'completed') return i;
    }
    return 0; // All completed, recommend first
  },
}));
